import {
	type FC,
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastItem {
	id: string;
	type: ToastType;
	message: string;
	duration?: number;
}

interface ToastContextValue {
	addToast: (type: ToastType, message: string, duration?: number) => void;
	removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};

let toastCounter = 0;

const typeStyles: Record<ToastType, { bg: string; icon: string }> = {
	success: { bg: "bg-success-soft border-success/30 text-success", icon: "M4.5 12.75l6 6 9-13.5" },
	error: { bg: "bg-danger-soft border-danger/30 text-danger", icon: "M6 18L18 6M6 6l12 12" },
	warning: {
		bg: "bg-warning-soft border-warning/30 text-warning",
		icon: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z",
	},
	info: {
		bg: "bg-primary-soft border-primary/20 text-primary",
		icon: "M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z",
	},
};

const ToastNotification: FC<{ toast: ToastItem; onDismiss: (id: string) => void }> = ({
	toast,
	onDismiss,
}) => {
	const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const duration = toast.duration ?? 5000;

	useEffect(() => {
		if (duration > 0) {
			timerRef.current = setTimeout(() => {
				onDismiss(toast.id);
			}, duration);
		}
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, [toast.id, duration, onDismiss]);

	const style = typeStyles[toast.type];

	return (
		<div
			role="alert"
			aria-live="assertive"
			className={`flex items-start gap-3 rounded-lg border p-4 shadow-sm ${style.bg}`}
		>
			{/* Decorative: the message beside it says the same thing. */}
			<svg
				aria-hidden="true"
				className="mt-0.5 h-5 w-5 shrink-0"
				fill="none"
				viewBox="0 0 24 24"
				strokeWidth="1.5"
				stroke="currentColor"
			>
				<path strokeLinecap="round" strokeLinejoin="round" d={style.icon} />
			</svg>
			<p className="flex-1 text-sm font-medium">{toast.message}</p>
			<button
				type="button"
				onClick={() => onDismiss(toast.id)}
				className="shrink-0 rounded-md p-0.5 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
				aria-label="Dismiss"
			>
				<svg
					aria-hidden="true"
					className="h-4 w-4"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth="1.5"
					stroke="currentColor"
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
				</svg>
			</button>
		</div>
	);
};

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = useState<ToastItem[]>([]);

	const addToast = useCallback((type: ToastType, message: string, duration?: number) => {
		const id = `toast-${++toastCounter}`;
		setToasts((prev) => [...prev, { id, type, message, duration }]);
	}, []);

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	return (
		<ToastContext.Provider value={{ addToast, removeToast }}>
			{children}
			<div
				aria-label="Notifications"
				className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2"
				style={{ maxWidth: "24rem" }}
			>
				{toasts.map((toast) => (
					<ToastNotification key={toast.id} toast={toast} onDismiss={removeToast} />
				))}
			</div>
		</ToastContext.Provider>
	);
};
